import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAccessToken, employeeApi } from '../lib/api.js';

const CurrentUserContext = createContext(null);

export function CurrentUserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const retriesRef = useRef(0);

  async function fetchProfile() {
    try {
      const token = await getAccessToken();

      // If no token yet and MSAL might still be initializing, retry
      if (!token) {
        if (retriesRef.current < 5) {
          retriesRef.current++;
          setTimeout(fetchProfile, 500 * retriesRef.current);
          return;
        }
        setUser(null);
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-ProHR-Token': `Bearer ${token}`,
      };
      const res = await fetch('/api/my/profile', { credentials: 'include', headers });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          // Token might be stale — retry once
          if (retriesRef.current < 3) {
            retriesRef.current++;
            setTimeout(fetchProfile, 1000);
            return;
          }
          setUser(null);
          return;
        }
        throw new Error(`Profile fetch failed: ${res.status}`);
      }
      const data = await res.json();
      setUser(data.profile || null);
      retriesRef.current = 0;

      // Best-effort: sync the server's copy of the user's timezone with the
      // browser so the daily approver digest lands at ~9am in their own local
      // time. Fire-and-forget — must never block or fail the profile load.
      try {
        const p = data.profile;
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (p && browserTz && p.timeZone !== browserTz) {
          employeeApi.setTimezone(browserTz).catch(() => {});
        }
      } catch { /* Intl unavailable — ignore */ }
    } catch (err) {
      console.error('useCurrentUser: fetch failed', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Wait a beat for MSAL to initialize
    const timer = setTimeout(fetchProfile, 300);
    return () => clearTimeout(timer);
  }, []);

  const value = {
    user,
    loading,
    error,
    refetch: fetchProfile,
    isAdmin: user?.role === 'admin',
    // Finance counts as an approver — they can approve expense
    // reports routed to them in a chain. Same behavior the backend
    // enforces in requireRole(APPROVER).
    isApprover: user?.role === 'approver' || user?.role === 'admin' || user?.role === 'finance',
    isEmployee: !!user,
    // Finance is a scoped admin: only the expense + position admin
    // pages should render for them. isAdmin stays narrow (true admin
    // only); anything that should ALSO open to finance checks
    // isFinance OR isAdmin.
    isFinance: user?.role === 'finance',
    // Section-level permissions from the configurable roles system. `sections`
    // is computed server-side (my-profile). admin ⇒ all sections. can() is the
    // going-forward gate for admin nav/pages; isAdmin/isFinance are kept for
    // existing call sites.
    sections: user?.sections || [],
    can: (section) => user?.role === 'admin' || (Array.isArray(user?.sections) && user.sections.includes(section)),
  };

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useCurrentUser must be used within CurrentUserProvider');
  return ctx;
}
